import { useEffect, useMemo, useState } from "react";
import { api, API_BASE, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import SignaturePad from "@/components/SignaturePad";
import { AUDIT_SECTIONS, WORK_TYPES, emptyAudit } from "@/lib/auditTemplate";
import { toast } from "sonner";
import { AlertTriangle, FileDown, Loader2, Save, X } from "lucide-react";

const RATINGS = [
  { code: "C",  label: "C",  pill: "bg-emerald-700 text-white" },
  { code: "NC", label: "NC", pill: "bg-red-700 text-white" },
  { code: "NA", label: "NA", pill: "bg-slate-700 text-slate-100" },
];

/**
 * Site Safety Audit form. Mirrors the Force2 docx 1:1. Auditor fills
 * sections inline; save is a single PATCH so partial progress sticks
 * across mid-walk reloads. "Generate PDF" enqueues a closeout-style job
 * and the page polls/downloads it like the other Force2 deliverables.
 */
export default function AuditForm({ project, onChanged }) {
  const [audit, setAudit] = useState(() => emptyAudit());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "save" | "pdf" | null
  // Signature pad workflow: capture a name (auditor / crew lead),
  // then drop the canvas data-URL into the audit's signatures array.
  // Same pattern as JSA / Tailgate forms for visual + UX parity.
  const [sigName, setSigName] = useState("");
  const [sigRole, setSigRole] = useState("Auditor");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${project.id}/audit`);
        // Merge any server-saved values onto the template — preserves
        // section order even if items were added to the template later.
        if (data?.audit) {
          setAudit({ ...emptyAudit(), ...data.audit, sections: mergeSections(data.audit.sections) });
        }
      } catch (e) {
        // No saved audit yet (404) is the common case — fresh form is
        // fine. Anything else (network, 500) is worth a console trail.
        if (e?.response?.status && e.response.status !== 404) {
          console.warn("[audit] failed to load existing audit", e);
        }
      }
      setLoading(false);
    })();
  }, [project.id]);

  const { totalRated, compliant, nonCompliant, pct, statusLabel } = useMemo(
    () => computeCompliance(audit), [audit],
  );

  const setField = (k, v) => setAudit((a) => ({ ...a, [k]: v }));

  const setRating = (sIdx, iIdx, code) => {
    setAudit((a) => {
      const sections = a.sections.slice();
      const sec = { ...sections[sIdx] };
      const items = sec.items.slice();
      // Toggle off if already set so a misclick is one-click recoverable.
      items[iIdx] = { ...items[iIdx], rating: items[iIdx].rating === code ? "" : code };
      sec.items = items;
      sections[sIdx] = sec;
      return { ...a, sections };
    });
  };

  const setSectionComment = (sIdx, text) => {
    setAudit((a) => {
      const sections = a.sections.slice();
      sections[sIdx] = { ...sections[sIdx], comments: text };
      return { ...a, sections };
    });
  };

  const toggleWorkType = (wt) => setAudit((a) => ({
    ...a,
    work_types: a.work_types.includes(wt)
      ? a.work_types.filter((x) => x !== wt)
      : [...a.work_types, wt],
  }));

  const addSignature = (dataUrl) => {
    if (!sigName.trim()) {
      toast.error("Enter the signer's name first.");
      return false;
    }
    setAudit((a) => ({
      ...a,
      signatures: [...(a.signatures || []), {
        name: sigName.trim(),
        role: sigRole,
        signature_data: dataUrl,
        signed_at: new Date().toISOString(),
      }],
    }));
    setSigName("");
  };

  const removeSignature = (idx) => setAudit((a) => ({
    ...a,
    signatures: (a.signatures || []).filter((_, i) => i !== idx),
  }));

  const save = async () => {
    setBusy("save");
    try {
      await api.post(`/projects/${project.id}/audit`, audit);
      toast.success("Audit saved");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  const generatePdf = async () => {
    setBusy("pdf");
    try {
      // Save first so the PDF reflects the latest edits.
      await api.post(`/projects/${project.id}/audit`, audit);
      const { data: job } = await api.post(`/projects/${project.id}/audit/generate`);
      // The audit PDF is rendered synchronously, so the job is `ready`
      // by the time we get the response — go straight to download.
      let url;
      try {
        const { signDownload } = await import("@/lib/signedUrl");
        url = await signDownload("closeout_job", job.id);
      } catch (_e) {
        url = `${API_BASE}/projects/${project.id}/closeout/jobs/${job.id}/download`;
      }
      window.location.href = url;
      toast.success("Audit PDF ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF generation failed");
    } finally { setBusy(null); }
  };

  if (loading) {
    return <div className="text-slate-400 text-sm">Loading audit…</div>;
  }

  return (
    <div className="space-y-5" data-testid="audit-form">
      {/* Header — auditor inputs */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-5 space-y-4">
          <h3 className="font-display tracking-wider uppercase text-sm text-slate-300">General Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Site ID" value={audit.site_id} onChange={(v) => setField("site_id", v)} placeholder={project.site_number} />
            <Field label="Audit Date" type="date" value={audit.audit_date} onChange={(v) => setField("audit_date", v)} />
            <Field label="Audit Time" type="time" value={audit.audit_time} onChange={(v) => setField("audit_time", v)} />
            <Field label="Auditor Name" value={audit.auditor_name} onChange={(v) => setField("auditor_name", v)} />
            <Field label="Crew Lead / Foreman" value={audit.crew_lead} onChange={(v) => setField("crew_lead", v)} placeholder={project.foreman} />
            <Field label="Technicians On-Site" value={audit.technicians_on_site} onChange={(v) => setField("technicians_on_site", v)} placeholder="comma-separated" />
          </div>

          <div>
            <Label>Type of Work Being Performed</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {WORK_TYPES.map((w) => {
                const on = audit.work_types.includes(w);
                return (
                  <button
                    key={w}
                    type="button"
                    onClick={() => toggleWorkType(w)}
                    className={`text-xs px-3 py-1.5 rounded-full border font-display tracking-wider uppercase transition-colors ${on ? "bg-blue-700 border-blue-600 text-white" : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"}`}
                    data-testid={`audit-worktype-${w.toLowerCase()}`}
                  >
                    {w}
                  </button>
                );
              })}
            </div>
            <Input
              value={audit.work_type_other}
              onChange={(e) => setField("work_type_other", e.target.value)}
              placeholder="Other (specify)"
              className="mt-2 bg-slate-950 border-slate-700"
            />
          </div>
        </CardContent>
      </Card>

      {/* Compliance summary — live updates as ratings flip */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-5 grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <Metric label="Items Rated" v={totalRated} />
          <Metric label="Compliant" v={compliant} tone="emerald" />
          <Metric label="Non-Compliant" v={nonCompliant} tone="red" />
          <Metric label="Compliance" v={`${pct}%`} tone="blue" big />
          <Metric label="Status" v={statusLabel} tone={pct >= 85 ? "emerald" : pct >= 75 ? "amber" : "red"} />
        </CardContent>
      </Card>

      {/* 9 checklist sections */}
      {AUDIT_SECTIONS.map((tpl, sIdx) => {
        const sec = audit.sections[sIdx] || { items: [], comments: "" };
        return (
          <Card key={tpl.key} className="bg-slate-900 border-slate-800">
            <CardContent className="p-5">
              <h3 className="font-display tracking-wider uppercase text-sm text-slate-300 mb-3">{tpl.title}</h3>
              <div className="space-y-1.5">
                {sec.items.map((it, iIdx) => (
                  <div key={it.key} className="flex items-center gap-3 py-1.5 border-b border-slate-800/60">
                    <div className="flex-1 text-sm text-slate-100">{it.label}</div>
                    <div className="flex gap-1.5">
                      {RATINGS.map((r) => (
                        <button
                          key={r.code}
                          type="button"
                          onClick={() => setRating(sIdx, iIdx, r.code)}
                          className={`w-9 h-7 rounded text-[11px] font-display font-bold tracking-wider transition ${it.rating === r.code ? r.pill : "bg-slate-800 border border-slate-700 text-slate-400 hover:bg-slate-700"}`}
                          data-testid={`audit-rate-${tpl.key}-${it.key}-${r.code}`}
                          aria-pressed={it.rating === r.code}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <Textarea
                value={sec.comments}
                onChange={(e) => setSectionComment(sIdx, e.target.value)}
                placeholder="Section comments / observations…"
                className="mt-3 bg-slate-950 border-slate-700 min-h-[60px]"
                data-testid={`audit-comments-${tpl.key}`}
              />
            </CardContent>
          </Card>
        );
      })}

      {/* Observations + corrective actions + stop-work */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-5 space-y-4">
          <h3 className="font-display tracking-wider uppercase text-sm text-slate-300">10. Safety Observations</h3>
          <div>
            <Label>Positive Safety Observations</Label>
            <Textarea value={audit.positive_observations} onChange={(e) => setField("positive_observations", e.target.value)} className="bg-slate-950 border-slate-700" />
          </div>
          <div>
            <Label>Deficiencies Identified</Label>
            <Textarea value={audit.deficiencies} onChange={(e) => setField("deficiencies", e.target.value)} className="bg-slate-950 border-slate-700" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label>Corrective Actions Required</Label>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => setField("corrective_actions", [
                  ...(audit.corrective_actions || []),
                  // Stable id so React's reconciler doesn't reuse the
                  // wrong DOM node when rows are reordered or one is
                  // removed mid-list. Index-as-key broke focus on the
                  // input above a deleted row.
                  { _rid: `ca_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
                    item: "", responsible_person: "", due_date: "" },
                ])}
                data-testid="audit-add-action"
              >+ Add</Button>
            </div>
            <div className="space-y-2 mt-2">
              {(audit.corrective_actions || []).map((ca, i) => {
                // Legacy rows (saved before _rid existed) fall back to a
                // composed key built from their fields — still better
                // than the raw index because the composition changes
                // when an earlier row is deleted.
                const rowKey = ca._rid || `legacy_${i}_${ca.item || "_"}_${ca.due_date || "_"}`;
                return (
                <div key={rowKey} className="grid grid-cols-12 gap-2">
                  <Input value={ca.item} placeholder="Action item"
                    onChange={(e) => setField("corrective_actions", audit.corrective_actions.map((x, j) => j === i ? { ...x, item: e.target.value } : x))}
                    className="col-span-6 bg-slate-950 border-slate-700"/>
                  <Input value={ca.responsible_person} placeholder="Responsible"
                    onChange={(e) => setField("corrective_actions", audit.corrective_actions.map((x, j) => j === i ? { ...x, responsible_person: e.target.value } : x))}
                    className="col-span-3 bg-slate-950 border-slate-700"/>
                  <Input value={ca.due_date} type="date"
                    onChange={(e) => setField("corrective_actions", audit.corrective_actions.map((x, j) => j === i ? { ...x, due_date: e.target.value } : x))}
                    className="col-span-2 bg-slate-950 border-slate-700"/>
                  <Button type="button" variant="ghost" className="col-span-1 text-red-300"
                    onClick={() => setField("corrective_actions", audit.corrective_actions.filter((_, j) => j !== i))}
                    aria-label="Remove">✕</Button>
                </div>
                );
              })}
            </div>
          </div>

          <div className={`rounded-md border p-3 ${audit.stop_work_issued ? "border-red-700 bg-red-950/40" : "border-slate-700 bg-slate-950"}`}>
            <label className="flex items-center gap-2 text-sm font-display tracking-wider uppercase">
              <input
                type="checkbox"
                checked={audit.stop_work_issued}
                onChange={(e) => setField("stop_work_issued", e.target.checked)}
                data-testid="audit-stop-work"
              />
              <AlertTriangle className="w-4 h-4 text-red-300" />
              Stop-Work Issued
            </label>
            {audit.stop_work_issued && (
              <Textarea
                value={audit.stop_work_reason}
                onChange={(e) => setField("stop_work_reason", e.target.value)}
                placeholder="Explain the stop-work condition. This notifies all Admins and PMs."
                className="mt-2 bg-slate-900 border-red-700"
                data-testid="audit-stop-work-reason"
              />
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-5 space-y-3">
          <h3 className="font-display tracking-wider uppercase text-sm text-slate-300">Signatures ({(audit.signatures || []).length})</h3>
          <div className="flex flex-wrap gap-2">
            {(audit.signatures || []).map((s, i) => (
              <div key={`${i}-${s.name}`} className="bg-slate-950 border border-slate-700 rounded-md p-2 flex items-center gap-2" data-testid={`audit-sig-${i}`}>
                <img src={s.signature_data} alt="" className="h-10 bg-white rounded" />
                <div className="text-xs">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-slate-400">{s.role || ""} · {new Date(s.signed_at).toLocaleString()}</div>
                </div>
                <button onClick={() => removeSignature(i)} className="text-slate-400 hover:text-red-400" aria-label="Remove signature"><X className="w-4 h-4"/></button>
              </div>
            ))}
          </div>
          <div className="bg-slate-950 border border-slate-800 rounded-md p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Signer name" value={sigName} onChange={(e) => setSigName(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="audit-sig-name"/>
              <select value={sigRole} onChange={(e) => setSigRole(e.target.value)} className="bg-slate-900 border border-slate-700 rounded px-3 text-sm" data-testid="audit-sig-role">
                <option>Auditor</option>
                <option>Crew Lead</option>
                <option>Foreman</option>
                <option>Customer Rep</option>
              </select>
            </div>
            <SignaturePad onSave={addSignature} testid="audit-sig-pad" />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2 sticky bottom-2 bg-slate-950/95 backdrop-blur border border-slate-800 rounded-lg p-3">
        <Button onClick={save} disabled={!!busy} data-testid="audit-save">
          {busy === "save" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <Save className="w-4 h-4 mr-1.5"/>}
          Save Audit
        </Button>
        <Button onClick={generatePdf} disabled={!!busy} className="bg-red-700 hover:bg-red-600" data-testid="audit-generate-pdf">
          {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <FileDown className="w-4 h-4 mr-1.5"/>}
          Generate Audit PDF
        </Button>
      </div>
    </div>
  );
}

// ---- helpers --------------------------------------------------------------
function mergeSections(serverSections) {
  // The server payload may be missing fields that were added to the template
  // later. Reconcile by anchoring on the template order and pulling saved
  // ratings/comments forward where keys match.
  const byKey = Object.fromEntries((serverSections || []).map((s) => [s.key, s]));
  return AUDIT_SECTIONS.map((tpl) => {
    const saved = byKey[tpl.key];
    if (!saved) {
      return {
        key: tpl.key, title: tpl.title, comments: "",
        items: tpl.items.map(([key, label]) => ({ key, label, rating: "" })),
      };
    }
    const savedItems = Object.fromEntries((saved.items || []).map((it) => [it.key, it]));
    return {
      key: tpl.key, title: tpl.title, comments: saved.comments || "",
      items: tpl.items.map(([key, label]) => ({
        key, label,
        rating: (savedItems[key] && savedItems[key].rating) || "",
      })),
    };
  });
}

function computeCompliance(audit) {
  let total = 0, c = 0, nc = 0;
  for (const s of audit.sections || []) {
    for (const it of s.items || []) {
      if (it.rating === "C") { total++; c++; }
      else if (it.rating === "NC") { total++; nc++; }
    }
  }
  const pct = total ? Math.round((c / total) * 1000) / 10 : 0;
  const label =
    pct >= 95 ? "Excellent" :
    pct >= 85 ? "Satisfactory" :
    pct >= 75 ? "Needs Improvement" :
    total ? "Unsatisfactory" : "Not Rated";
  return { totalRated: total, compliant: c, nonCompliant: nc, pct, statusLabel: label };
}

function Field({ label, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        type={type} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="bg-slate-950 border-slate-700"
      />
    </div>
  );
}

function Label({ children }) {
  return <label className="text-[11px] uppercase tracking-wider text-slate-400 font-display font-bold">{children}</label>;
}

function Metric({ label, v, tone = "slate", big = false }) {
  const colors = {
    emerald: "text-emerald-300",
    red: "text-red-300",
    blue: "text-blue-300",
    amber: "text-amber-300",
    slate: "text-slate-200",
  };
  return (
    <div>
      <div className={`font-display ${big ? "text-3xl" : "text-2xl"} font-bold ${colors[tone]}`}>{v}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">{label}</div>
    </div>
  );
}
